<template>
  <div class="data-interaction">
    <div class="di-container">
      <!-- 左上角：今日数据 -->
      <div class="di-header-left">
        <div class="di-title">今日数据</div>
        <div class="di-stats" @click="goToDetail">
            <span class="stat-num">{{ totalCount }}</span>
            <span class="stat-unit">条</span>
            <span class="stat-num">{{ totalFlow }}</span>
            <span class="stat-unit">GB</span>
        </div>
      </div>

      <!-- 右上角：控制图标 -->
      <div class="di-header-right">
        <div class="di-icon-btn" title="管理" @click="goToManage">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
          <span>管理</span>
        </div>
        <div class="di-icon-btn" title="退出" @click="goHome">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
          <span>退出</span>
        </div>
      </div>

      <!-- 左侧地点列表 -->
      <div class="di-side-panel di-panel-left" v-show="showLeftPanel">
        <div
          v-for="item in projectList"
          :key="item.code"
          class="di-location-item"
          @click="selectProject(item)"
        >
          <span class="loc-dot" :style="{ backgroundColor: item.color }"></span>
          <span class="loc-name">{{ item.name }}</span>
          <span class="loc-count">{{ item.messageCount }}</span>
        </div>
      </div>

      <!-- 右侧地点列表 -->
      <div class="di-side-panel di-panel-right">
        <div
          v-for="item in projectList"
          :key="item.name"
          class="di-location-item"
          @click="showProject(item)"
        >
          <span class="loc-dot" :style="{ backgroundColor: item.color }"></span>
          <span class="loc-name">{{ item.name }}</span>
          <span class="loc-count">{{ item.messageCount }}</span>
        </div>
      </div>

      <!-- 中间地球 -->
      <div class="di-globe-placeholder">
        <Map ref="earthMap" :projectList="projectList" />
      </div>

      <!-- 右侧控制按钮 -->
      <div class="di-control-btns">
        <div class="ctrl-btn" title="3D/2D切换" @click="toggleViewMode">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="3" y1="15" x2="21" y2="15" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <line x1="15" y1="3" x2="15" y2="21" />
          </svg>
        </div>

        <div class="ctrl-btn" title="放大" @click="zoomIn">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </div>
        <div class="ctrl-btn" title="缩小" @click="zoomOut">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </div>
        <div
          class="ctrl-btn"
          :title="earthRotating ? '暂停' : '开始旋转'"
          @click="toggleRotation"
        >
          <!-- 自转中：显示暂停 -->
          <svg v-if="earthRotating" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="9" y1="5" x2="9" y2="19" />
            <line x1="15" y1="5" x2="15" y2="19" />
          </svg>
          <!-- 已暂停：显示播放 -->
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round">
            <polygon points="6 4 20 12 6 20" />
          </svg>
        </div>

      </div>

      <!-- 底部协议类型栏 -->
      <div class="di-bottom-bar">
        <div
          v-for="proto in protocolList"
          :key="proto.name"
          class="di-proto-item"
          :style="{ borderColor: proto.color }"
        >
          <div class="proto-count" :key="proto.name">{{ proto.value }}<span v-if="proto.unit" class="proto-unit"> {{ proto.unit }}</span></div>
          <div class="proto-name" :style="{ color: proto.color }">{{ proto.name }}</div>
        </div>
      </div>
    </div>

    <!-- 项目实时数据弹框 -->
    <div
      v-if="showProjectDetail"
      class="modal-mask"
      @click.self="closeProjectDetail"
    >
      <div class="modal-panel">
        <!-- 弹框头部 -->
        <div class="modal-header">
          <div class="modal-title">
            <span
              class="title-dot"
              :style="{ backgroundColor: selectedProject?.color }"
            ></span>
            <span class="title-name">{{ selectedProject?.name }}</span>
            <span class="title-sub">实时数据</span>
          </div>

          <div class="modal-search">
            <input
              v-model="msgSearch"
              type="text"
              placeholder="搜索..."
              class="search-input"
            />
            <svg viewBox="0 0 24 24" width="14" height="14" class="filter-icon">
              <path d="M3 6h18M6 12h12M10 18h4" stroke="currentColor" stroke-width="2" fill="none" />
            </svg>
          </div>

          <div class="modal-actions">
            <div
              class="action-btn"
              :class="{ active: !msgPaused }"
              @click="toggleMsgPause"
              :title="msgPaused ? '继续接收' : '暂停接收'"
            >
              <svg v-if="msgPaused" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <polygon points="6,4 20,12 6,20" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            </div>
            <div class="action-btn" @click="downloadMessages" title="下载 JSON">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7,10 12,15 17,10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </div>
            <div class="action-btn close-btn" @click="closeProjectDetail" title="关闭">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
          </div>
        </div>

        <!-- 消息列表 -->
        <div class="modal-body">
          <div
            v-for="msg in filteredMessages"
            :key="msg.id"
            class="msg-item"
          >
            <div class="msg-meta">
              <span class="msg-time">{{ msg.time }}</span>
              <span class="msg-from">{{msg.reportType || '--'}}</span>
              <span class="msg-source">{{ msg.source }}</span>
            </div>
            <pre class="msg-content">{{ formatDisplay(msg) }}</pre>
            <div class="msg-actions">
              <div class="msg-btn" @click="copyMessage(msg)" title="复制原始 JSON">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </div>
              <div
                class="msg-btn"
                :class="{ active: msg.beautified }"
                @click="toggleBeautify(msg)"
                :title="msg.beautified ? '压缩格式' : '美化格式'"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="12,2 15,9 22,9 16,14 18,22 12,18 6,22 8,14 2,9 9,9" />
                </svg>
              </div>
            </div>
          </div>
          <div v-if="!filteredMessages.length" class="msg-empty">
            暂无实时数据...
          </div>
        </div>

        <!-- 底部状态条 -->
        <!-- <div class="modal-footer">
          <span class="footer-count">共 {{ filteredMessages.length }} 条</span>
          <span
            class="footer-status"
            :class="{ paused: msgPaused }"
          >
            {{ msgPaused ? '● 已暂停' : '● 接收中' }}
          </span>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
import Map from "./map.vue";
import { post, get } from "@/api/apiClient";

export default {
  name: "DataInteraction",
  components: {
    Map,
  },
  data() {
    return {
      totalCount: 0,
      totalFlow: 0,
      oldTotalCount: 0,
      oldTotalFlow: 0,
      showLeftPanel: false,
      // 地球自转状态
      earthRotating: true,
      projectList: [
        /* { name: "虹桥机场", color: "#ff6b6b", count: 31, code: "SF123133", latitude: 31.2036, longitude: 121.3479 },
        { name: "深圳机场", color: "#4dabf7", count: 31, code: "SZ001", latitude: 22.6446, longitude: 113.8116 }, */
      ],
      protocolList: [
        /* { name: "KAFKA", color: "#22b8cf", count: 21314 },
        { name: "MQTT", color: "#ffd43b", count: 21314 },
        { name: "光电", color: "#51cf66", count: 21314 },
        { name: "手持式", color: "#ff922b", count: 21314 },
        { name: "背包式", color: "#cc5de8", count: 21314 },
        { name: "车载式", color: "#ff6b6b", count: 21314 }, */
      ],
      // 项目实时数据弹框
      showProjectDetail: false,
      selectedProject: null,
      realtimeMessages: [],
      msgPaused: false,
      msgSearch: "",
      msgPollTimer: null,
      msgIdCounter: 0,
      // 数据源开关：true=模拟数据，false=WebSocket（后期接入时改 false 即可）
      useMock: true,
      ws: null,
      // 前端存储上限（条）
      msgMaxStore: 500,
      // 数据轮询定时器
      dataTimer: null,
    };
  },
  mounted() {
    this.startDataPolling();
    this.$nextTick(() => {
      if (this.$refs.earthMap) {
        this.$refs.earthMap.initScene();
      }
    });
  },
  beforeUnmount() {
    this.stopDataPolling();
    // 清理所有未完成的数字动画帧
    if (this._animRafIds) {
      Object.values(this._animRafIds).forEach((id) => cancelAnimationFrame(id));
      this._animRafIds = {};
    }
    this.stopDataSource();
  },
  watch: {
    // 关闭左侧项目列表面板时，地球恢复自转
    showLeftPanel(val) {
      if (!val && this.$refs.earthMap) {
        this.$refs.earthMap.resumeEarthRotation();
        this.earthRotating = true;
      }
    },
  },
  computed: {
    // 过滤后的消息列表
    filteredMessages() {
      if (!this.msgSearch) return this.realtimeMessages;
      const kw = this.msgSearch.toLowerCase();
      return this.realtimeMessages.filter(
        (m) =>
          m.source.toLowerCase().includes(kw) /* ||
          m.raw.toLowerCase().includes(kw) */
      );
    },
  },
  methods: {
    // 打开项目实时数据弹框（右侧列表点击）
    showProject(item) {
      this.selectedProject = item;
      this.realtimeMessages = [];
      this.msgSearch = "";
      this.msgPaused = false;
      this.showProjectDetail = true;
      this.startDataSource();
    },
    // 关闭弹框
    closeProjectDetail() {
      this.showProjectDetail = false;
      this.stopDataSource();
      this.msgPaused = false;
    },
    // 暂停/继续接收
    toggleMsgPause() {
      this.msgPaused = !this.msgPaused;
    },
    // 启动数据源（模拟 or WebSocket，由 useMock 控制）
    startDataSource() {
      // this.startMockPolling();
      
      this.startWebSocket();
    },
    // 停止数据源
    stopDataSource() {
      this.stopMockPolling();
      this.stopWebSocket();
    },
    // ===== 统一数据入口：模拟数据和 WebSocket 都走这里 =====
    addMessage(rawData, source) {
      if (this.msgPaused) {
        console.warn("[addMessage] 消息被丢弃：msgPaused=true");
        return;
      }
      if (!this.showProjectDetail) {
        console.warn("[addMessage] 消息被丢弃：弹框已关闭");
        return;
      }
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const ss = String(now.getSeconds()).padStart(2, "0");

      // rawData 可能是字符串或对象，统一转成字符串存储
      const raw =
        typeof rawData === "string" ? rawData : JSON.stringify(rawData);
      const reportType = JSON.parse(rawData).reportType || '--';
      const reportSource = JSON.parse(rawData).projectData?.dataSource || source || this.selectedProject?.code || "unknown";

      this.realtimeMessages.unshift({
        id: this.msgIdCounter++,
        time: `${hh}:${mm}:${ss}`,
        reportType: reportType,
        source: reportSource,
        raw,
        beautified: false,
      });

      // 前端存储上限，超出截断旧数据
      if (this.realtimeMessages.length > this.msgMaxStore) {
        this.realtimeMessages.length = this.msgMaxStore;
      }
    },
    // 下载当前消息为 JSON 文件（从前端存储导出）
    downloadMessages() {
      const exportData = this.filteredMessages.map((m) => {
        let parsed;
        try { parsed = JSON.parse(m.raw); } catch { parsed = m.raw; }
        return { time: m.time, source: m.source, data: parsed };
      });
      const blob = new Blob(
        [JSON.stringify(exportData, null, 2)],
        { type: "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${this.selectedProject?.code || "project"}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    // 复制某条消息原始 JSON
    copyMessage(msg) {
      const text = msg.raw;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast
            ? this.$toast.success("已复制")
            : alert("已复制到剪贴板");
        })
        .catch(() => {
          const ta = document.createElement("textarea");
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          alert("已复制");
        });
    },
    // 切换单条消息的美化格式
    toggleBeautify(msg) {
      msg.beautified = !msg.beautified;
    },
    // ===== 模拟数据（后期接 WebSocket 时设 useMock=false 即可跳过） =====
    startMockPolling() {
      this.stopMockPolling();
      // 立即先推一条
      this.addMessage(this.generateMockRaw(), this.generateMockSource());
      this.msgPollTimer = setInterval(() => {
        this.addMessage(this.generateMockRaw(), this.generateMockSource());
      }, 1200);
    },
    stopMockPolling() {
      if (this.msgPollTimer) {
        clearInterval(this.msgPollTimer);
        this.msgPollTimer = null;
      }
    },
    // 生成模拟来源
    generateMockSource() {
      const sources = [
        this.selectedProject?.code || "DEV001",
        "天御系统",
        "预警平台",
        "SF1231332122",
        "采集节点",
      ];
      return sources[Math.floor(Math.random() * sources.length)];
    },
    // 生成模拟 JSON 字符串（两种格式随机）
    generateMockRaw() {
      const source = this.generateMockSource();
      if (Math.random() > 0.5) {
        return JSON.stringify({
          id: Date.now() + "_" + this.msgIdCounter++,
          from: source,
          ok: Math.random() > 0.1,
          payload: "x".repeat(32 + Math.floor(Math.random() * 32)),
          ts: new Date().toISOString(),
        });
      } else {
        return JSON.stringify({
          msgId: "MSG" + (Date.now() % 100000),
          deviceCode: this.selectedProject?.code || "DEV001",
          lat: Number(this.selectedProject?.latitude || 0) + (Math.random() - 0.5) * 0.01,
          lon: Number(this.selectedProject?.longitude || 0) + (Math.random() - 0.5) * 0.01,
          signal: -40 - Math.floor(Math.random() * 50),
          speed: Math.floor(Math.random() * 120),
          detail: "x".repeat(30 + Math.floor(Math.random() * 30)),
        });
      }
    },
    // ===== WebSocket =====
    async startWebSocket() {
      try {
        const res = await get(`/admin/dataSwapProject/getWsUrl/${this.selectedProject?.code}`);
        if (res.data.code !== 200 || !res.data.data) {
          return;
        }
        // 已有连接先关闭
        if (this.ws) this.stopWebSocket();
        const url = res.data.data;
        // const url = 'ws://47.107.35.147:10002/projectData';
        this.ws = new WebSocket(url);
        this.ws.onopen = () => {
          console.log("[WS] 连接已建立 ✅", url);
        };

        this.ws.onerror = (err) => {
          console.error("[WS] 连接错误 ❌", err);
        };
 
        this.ws.onmessage = async (event) => {
          // this.addMessage(event.data, this.selectedProject?.code);
          // Blob/ArrayBuffer 必须先转文本
          let text;
          if (event.data instanceof Blob) {
            text = await event.data.text();
          } else if (event.data instanceof ArrayBuffer) {
            text = new TextDecoder().decode(event.data);
          } else {
            text = event.data; // 字符串
          }
          console.log("[WS] 收到消息:", text?.slice(0, 120));
          this.addMessage(text, this.selectedProject?.code);
        };

        this.ws.onclose = (e) => {
          console.log(`[WS] 连接已关闭 code=${e.code} reason=${e.reason || "无"}`);
          this.ws = null;
        };
      } catch (err) {
        console.error("[WS] startWebSocket 异常:", err);
      }
    },
    stopWebSocket() {
      if (this.ws) {
        this.ws.onmessage = null;
        this.ws.onclose = null;
        this.ws.close();
        this.ws = null;
      }
    },
    // 格式化显示：美化 or 压缩
    formatDisplay(msg) {
      if (msg.beautified) {
        try {
          return JSON.stringify(JSON.parse(msg.raw), null, 2);
        } catch {
          return msg.raw;
        }
      }
      return msg.raw;
    },
    selectProject(item) {
      // 打开左侧项目列表面板，地图飞行定位并停止自转
      this.showLeftPanel = true;
      this.earthRotating = false;
      this.$refs.earthMap.selectProject(item);
    },
    // 数据轮询（5s 一次）
    startDataPolling() {
      this.stopDataPolling();
      this.initData();
      this.dataTimer = setInterval(() => {
        this.initData();
      }, 5000);
    },
    stopDataPolling() {
      if (this.dataTimer) {
        clearInterval(this.dataTimer);
        this.dataTimer = null;
      }
    },
    // 数字缓动动画（easeOutCubic），通过 setter 回调写入展示值
    animateValue(key, setter, from, to, duration = 800, decimals = 0) {
      // console.log(key,setter, from, to, duration, decimals,'animateValue');
      if (!this._animRafIds) this._animRafIds = {};
      if (this._animRafIds[key]) {
        cancelAnimationFrame(this._animRafIds[key]);
      }
      const start = performance.now();
      const easeOut = (t) => 1 - Math.pow(1 - t, 3);
      const step = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const v = from + (to - from) * easeOut(p);
        setter(decimals > 0 ? Number(v.toFixed(decimals)) : Math.round(v));
        if (p < 1) {
          this._animRafIds[key] = requestAnimationFrame(step);
        } else {
          setter(to);
          delete this._animRafIds[key];
        }
      };
      this._animRafIds[key] = requestAnimationFrame(step);
    },
    // 解析 "4 MB" → { value: 4, unit: "MB" }
    parseSizeStr(str) {
      if (str == null) return { value: 0, unit: "" };
      const s = String(str).trim();
      const m = s.match(/^([\d.]+)\s*(.*)$/);
      if (!m) return { value: 0, unit: "" };
      return { value: parseFloat(m[1]) || 0, unit: m[2] || "" };
    },
    // 获取项目列表
    async initData() {
      const res = await get("/admin/dataSwapProject/getHomePageDetail");
      console.log(res,'项目数据');
      if (res.data.code === 200) {
        this.oldTotalCount = this.totalCount;
        this.oldTotalFlow = this.totalFlow;
        const oldProtocolList = this.protocolList;
        this.totalCount = res.data.data.totalCount;
        this.totalFlow = parseFloat(res.data.data.totalFlow);
        this.projectList = res.data.data.projectInfo;
        const dd = res.data.data.deviceDetail[0];
        this.protocolList = [
          { name: "KAFKA", color: "#22b8cf", ...this.parseSizeStr(dd.kafka) },
          { name: "MQTT", color: "#ffd43b", ...this.parseSizeStr(dd.mqtt) },
          { name: "光电", color: "#51cf66", ...this.parseSizeStr(dd.pe) },
          { name: "手持式", color: "#ff922b", ...this.parseSizeStr(dd.handheld) },
          { name: "背包式", color: "#cc5de8", ...this.parseSizeStr(dd.knapsack) },
          { name: "车载式", color: "#ff6b6b", ...this.parseSizeStr(dd.vehicleMounted) },
        ];

        // 顶部数字动画
        const flowDecimals = String(res.data.data.totalFlow).includes(".") ? 2 : 0;
        this.animateValue("count", (v) => (this.totalCount = v), this.oldTotalCount, this.totalCount, 800, 0);
        this.animateValue("flow", (v) => (this.totalFlow = v), this.oldTotalFlow, this.totalFlow, 800, flowDecimals);

        // 底部协议数字动画
        this.protocolList.forEach((p, i) => {
          this.animateValue("proto" + i, (v) => (this.protocolList[i].value = v), oldProtocolList[i]?.value || 0, p.value, 500, 0);
        });

        // 项目列表 messageCount
        this.projectList.forEach((p, i) => {
          const target = Number(p.messageCount) || 0;
          this.animateValue("proj" + (p.code || i), (v) => (this.projectList[i].messageCount = v), 0, target, 800, 0);
        });
      }
    },
    goToDetail() {
      this.showLeftPanel = !this.showLeftPanel;
    },
    goToManage() {
      this.$router.push("/data-manage");
    },
    goHome() {
      this.$router.push("/");
    },
    zoomIn() {
      if (this.$refs.earthMap) {
        this.$refs.earthMap.zoomIn();
      }
    },
    zoomOut() {
      if (this.$refs.earthMap) {
        this.$refs.earthMap.zoomOut();
      }
    },
    toggleRotation() {
      if (this.$refs.earthMap) {
        this.$refs.earthMap.toggleEarthRotation();
        this.earthRotating = this.$refs.earthMap.earthRotating;
      }
    },
    toggleViewMode() {
      if (this.$refs.earthMap) {
        this.$refs.earthMap.toggleViewMode();
      }
    },
  },
};
</script>

<style scoped lang="less">
.data-interaction {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #05080f;
  color: #fff;
  font-family: "Microsoft YaHei", sans-serif;
}

/* ===== 主容器 ===== */
.di-container {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

/* ===== 左上角：今日数据 ===== */
.di-header-left {
  position: absolute;
  top: 60px;
  left: 30px;
  z-index: 5;
}

.di-title {
  font-size: 16px;
  color: #8ecae6;
  margin-bottom: 10px;
  letter-spacing: 2px;
}

.di-stats {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.stat-num {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 0 10px rgba(64, 169, 255, 0.6);
  display: inline-block;
  animation: numPulse 0.4s ease-out;
}

@keyframes numPulse {
  0% {
    transform: scale(1.15);
    text-shadow: 0 0 16px rgba(64, 169, 255, 0.9);
  }
  100% {
    transform: scale(1);
    text-shadow: 0 0 10px rgba(64, 169, 255, 0.6);
  }
}

.stat-unit {
  font-size: 14px;
  color: #a0c4ff;
  margin-right: 10px;
}

/* ===== 右上角：控制图标 ===== */
.di-header-right {
  position: absolute;
  top: 10px;
  right: 30px;
  display: flex;
  gap: 24px;
  z-index: 5;
}

.di-icon-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  color: #8ecae6;
  transition: color 0.3s;

  span {
    font-size: 12px;
    margin-top: 4px;
  }

  &:hover {
    color: #48cae4;
  }
}

/* ===== 左右侧地点列表面板 ===== */
.di-side-panel {
  position: absolute;
  top: 160px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.di-panel-left {
  left: 30px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  &::after {
    content: "";
    position: absolute;
    top: -10px;
    left: 20%;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 9px solid rgba(255, 255, 255, 0.8);
  }
}

.di-panel-right {
  right: 30px;
  max-height: 200px;
  overflow-y: visible;
}

.di-location-item {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.14);
  border-radius: 4px;
  min-width: 150px;
  cursor: pointer;
}

.loc-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 10px;
  flex-shrink: 0;
}

.loc-name {
  flex: 1;
  font-size: 14px;
  color: #e0e7ff;
}

.loc-count {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
}

/* ===== 中间地球区 ===== */
.di-globe-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  // transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* ===== 右侧控制按钮 ===== */
.di-control-btns {
  position: absolute;
  top: 50%;
  right: 30px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 5;
}

.ctrl-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(100, 180, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  color: #8ecae6;
  transition: all 0.3s;

  &:hover {
    background: rgba(100, 180, 255, 0.15);
    color: #48cae4;
  }
}

/* ===== 底部协议类型栏 ===== */
.di-bottom-bar {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 16px;
  z-index: 5;
}

.di-proto-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 110px;
  padding: 10px 20px;
  background: rgba(10, 20, 40, 0.6);
  border: 1px solid;
  border-radius: 4px;
}

.proto-count {
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  margin-bottom: 6px;
  display: inline-block;
  animation: numPulse 0.4s ease-out;
}

// .proto-unit {
//   font-size: 12px;
//   font-weight: normal;
//   color: #a0c4ff;
//   margin-left: 2px;
// }

.proto-name {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
}

/* ===== 项目实时数据弹框 ===== */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  // backdrop-filter: blur(1px);
}

.modal-panel {
  width: 980px;
  padding: 0 15px;
   max-width: 90vw;
  height: 680px;
  max-height: 82vh;
  background: rgba(10, 18, 35, 0.8);
  border: 1px solid rgba(100, 180, 255, 0.25);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
  animation: modalFadeIn 0.2s ease;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.modal-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 14px 0px;
  // background: rgba(100, 180, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.7);
  gap: 16px;
  justify-content: space-between;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.title-dot {
  width: 12px;
  height: 12px;
  border-radius: 1px;
  // box-shadow: 0 0 8px currentColor;
}

.title-name {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.title-sub {
  font-size: 13px;
  color: #fff;
  letter-spacing: 1px;
}

.modal-search {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  max-width: 280px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
  height: 30px;
  padding: 0 28px 0 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(100, 180, 255, 0.25);
  border-radius: 4px;
  color: #fff;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder { color: rgba(255, 255, 255, 0.35); }
  &:focus { border-color: rgba(100, 180, 255, 0.6); }
}

.filter-icon {
  position: absolute;
  right: 8px;
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.modal-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  // background: rgba(255, 255, 255, 0.05);
  // border: 1px solid rgba(100, 180, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    // background: rgba(100, 180, 255, 0.15);
    color: #48cae4;
    // border-color: rgba(100, 180, 255, 0.5);
  }

  &.active {
    color: #48cae4;
  }

  &.close-btn:hover {
    // background: rgba(255, 100, 100, 0.2);
    color: #ff6b6b;
    // border-color: rgba(255, 100, 100, 0.5);
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 180, 255, 0.3);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track { background: transparent; }
}

.msg-item {
  position: relative;
  padding: 10px 80px 10px 14px;
  margin-bottom: 10px;
  // background: rgba(255, 255, 255, 0.03);
  // border: 1px solid rgba(100, 180, 255, 0.12);
  // border-radius: 5px;
  transition: border-color 0.2s, background 0.2s;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-family: "Arial", sans-serif;

  &:hover {
    background: rgba(100, 180, 255, 0.06);
    border-color: rgba(100, 180, 255, 0.3);
  }
}

.msg-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  font-size: 12px;
}

.msg-time,
.msg-source {
  font-size: 14px;
  color: #fff;
}
.msg-source{
  width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.msg-from {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.msg-content {
  flex: 1;
  margin: 0;
  // font-family: "Consolas", "Menlo", monospace;
  font-size: 13px;
  line-height: 1.55;
  color: #fff;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 160px;
  overflow-y: auto;

  &::-webkit-scrollbar { height: 4px; width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 180, 255, 0.3);
    border-radius: 2px;
  }
}

.msg-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
}

.msg-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  // background: rgba(255, 255, 255, 0.04);
  // border: 1px solid rgba(100, 180, 255, 0.15);
  border-radius: 3px;
  color: rgba(255, 255, 255, 1);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    // background: rgba(100, 180, 255, 0.2);
    color: #48cae4;
  }

  &.active {
    color: #ffd43b;
    // background: rgba(255, 212, 59, 0.12);
    border-color: rgba(255, 212, 59, 0.4);
  }
}

.msg-empty {
  padding: 40px;
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
}

.modal-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
  background: rgba(10, 18, 35, 0.9);
  border-top: 1px solid rgba(100, 180, 255, 0.15);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.footer-count {
  color: #8ecae6;
}

.footer-status {
  color: #51cf66;
  font-family: "Consolas", monospace;

  &.paused {
    color: #ff922b;
  }
}
</style>
